"use client";

import { Drawer, DrawerContent, DrawerTitle } from "@/shadcn/ui/drawer";
import { VideoEvents, trackEvent } from "@/analytics";

import ExpandableText from "@/components/common/ExpandableText";
import { useMediaQuery } from "@/components/common/hooks/useMediaQuery";
import { useState } from "react";

interface DescriptionDisplayProps {
  description: string;
  className?: string;
}

export function DescriptionDisplay({
  description,
  className = "",
}: DescriptionDisplayProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Desktop: Use inline expandable text
  if (isDesktop) {
    return (
      <div className={className}>
        <ExpandableText
          className="text-sm text-gray-500"
          text={description}
          wordsToShow={60}
          limitParagraphsToShow={6}
        />
      </div>
    );
  }

  // Mobile: Show truncated text with drawer
  const truncateText = (
    text: string,
    maxWords: number,
    maxParagraphs: number
  ) => {
    const paragraphs = text.split(/\n\n+/);
    const limitedParagraphs = paragraphs.slice(0, maxParagraphs);
    const combinedText = limitedParagraphs.join("\n\n");

    const words = combinedText.split(/\s+/);
    if (words.length <= maxWords) {
      return combinedText;
    }

    return words.slice(0, maxWords).join(" ") + "...";
  };

  const truncatedDescription = truncateText(description, 40, 3);
  const isTruncated = truncatedDescription !== description;

  const handleOpenDrawer = () => {
    trackEvent(VideoEvents.CLICKED_DESCRIPTION);
    setIsDrawerOpen(true);
  };

  return (
    <>
      <div className={className}>
        <button
          onClick={handleOpenDrawer}
          className="text-left w-full hover:text-gray-700 transition-colors"
          aria-label="View full description"
        >
          <p className="text-sm text-gray-500 line-clamp-2">{description}</p>
          <span className="text-sm text-gray-500 hover:text-gray-700 font-bold">
            See more
          </span>
        </button>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerContent className="max-h-[85vh] p-6">
          <div className="mx-auto w-full max-w-3xl flex flex-col overflow-hidden">
            <DrawerTitle className="text-lg font-semibold pb-4">
              Description
            </DrawerTitle>
            <div className="overflow-y-auto flex-1">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {description}
              </p>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
